/**
 * Mindmap View - Zoom and Pan Module
 *
 * Transform management, wheel zoom, button controls, canvas panning.
 * Uses callback injection pattern for dependencies.
 */

import * as State from './state.js';
import * as MmCanvas from "./canvas-renderer.js";
import { isMobileShellVisible } from "../../../../shared/utils/device-detect.js";
import * as Store from '../../store/index.js';
import { t } from '../../../../shared/i18n/index.js';
import { visibleViewRect } from '../../ui/view-centering.js';

// ============================================================
// Module callbacks (injected via init)
// ============================================================
let zoomCallbacks = null;

/**
 * Initialize zoom/pan module with callbacks
 * @param {Object} cbs
 * @param {Function} cbs.onNavigate - Navigate to node (for zoomTo)
 * @param {Function} cbs.triggerRender - Trigger mindmap render
 * @param {Function} cbs.increaseGap - Increase gap (gap controls)
 * @param {Function} cbs.decreaseGap - Decrease gap (gap controls)
 */
export function init(cbs) {
  zoomCallbacks = cbs;
}

// ============================================================
// Transform Management
// ============================================================

/**
 * Update world transform (zoom/pan)
 * Note: Only transform the world (nodes), not the SVG
 * SVG paths need to be recalculated based on transformed node positions
 */
// The world's zoom transition (.mm-world.mm-animate) runs only for the user's
// own zoom (wheel, buttons, keys, reset), and only while the map shows at
// most this many nodes: while the transform animates, the browser groups
// every node's painting into layers again (2,000 nodes: about 0.26 s; 20,000
// nodes: 11 s when the view opened, which animated from no transform).
const ANIMATE_MAX_NODES = 1000;

export function updateTransform(options = {}) {
  // Canvas mode reads pan/zoom at paint time — repaint on every change.
  MmCanvas.markDirty();
  if (State.world) {
    State.world.classList.toggle("mm-animate", !!options.animate && State.world.childElementCount <= ANIMATE_MAX_NODES);
    State.world.style.transform = `translate(${State.mState.panX}px, ${State.mState.panY}px) scale(${State.mState.zoom})`;
  }

  const zlvl = State.container?.querySelector("#m-zlvl");
  if (zlvl) {
    zlvl.textContent = Math.round(State.mState.zoom * 100) + "%";
  }

  // No need to update paths - SVG is inside world and transforms with it

  // Save to Store (debounced, only if changed). Mobile embedded previews pass
  // save=false so viewing/pinching a preview cannot overwrite the user's saved
  // desktop mindmap transform.
  if (options.save !== false) {
    saveTransformToStore();
  }
}

/**
 * Save transform state to Store (debounced, only if changed)
 */
function saveTransformToStore() {
  // Mobile previews (pinch/pan/fit on the embedded view) mutate mState but
  // must NEVER persist: doRender ends with updateTransform() (save=true), so
  // without this guard every mobile render leaked the preview transform into
  // the Store — the desktop then restored at e.g. 2% zoom and looked blank.
  if (isMobileShellVisible()) return;
  if (State.transformSaveTimeout) {
    clearTimeout(State.transformSaveTimeout);
  }
  State.setTransformSaveTimeout(setTimeout(() => {
    // Only save if values actually changed
    if (
      State.mState.zoom !== State.lastSavedTransform.zoom ||
      State.mState.panX !== State.lastSavedTransform.panX ||
      State.mState.panY !== State.lastSavedTransform.panY
    ) {
      const newTransform = {
        zoom: State.mState.zoom,
        panX: State.mState.panX,
        panY: State.mState.panY,
      };
      State.setLastSavedTransform(newTransform);
      Store.setMindmapTransform(newTransform);
    }
  }, 1000));
}

// ============================================================
// Zoom Operations
// ============================================================

/**
 * Zoom towards a specific point (or center if no point given)
 */
export function zoomTowards(zoomDelta, centerX, centerY) {
  if (!State.canvas) return;

  const rect = State.canvas.getBoundingClientRect();

  // No point given (the buttons): the middle of what is visible, which the
  // notes pane may narrow; the canvas's own center drifted under it.
  if (centerX === undefined || centerY === undefined) {
    const visible = visibleViewRect(State.canvas);
    centerX = visible ? visible.x - rect.left - State.canvas.clientLeft : rect.width / 2;
    centerY = visible ? visible.y - rect.top - State.canvas.clientTop : rect.height / 2;
  }

  // Calculate world coordinates of center point before zoom
  const worldX = (centerX - State.mState.panX) / State.mState.zoom;
  const worldY = (centerY - State.mState.panY) / State.mState.zoom;

  // Apply zoom
  const newZoom = Math.max(0.1, Math.min(3, State.mState.zoom * zoomDelta));

  // Don't update if stuck at limit
  if (newZoom === State.mState.zoom) {
    return;
  }

  // Adjust pan so the same world point stays under the center
  State.setMState({
    panX: centerX - worldX * newZoom,
    panY: centerY - worldY * newZoom,
    zoom: newZoom
  });

  updateTransform({ animate: true });
}

/**
 * Zoom into a node
 */
export function zoomTo(id) {
  State.setCurrentState({ ...State.currentState, focusId: id });
  State.setMState({ zoom: 1, panX: 0, panY: 0 });

  if (zoomCallbacks?.onNavigate) {
    zoomCallbacks.onNavigate(id);
  }

  setTimeout(() => zoomCallbacks?.triggerRender?.(), 0);
}

/**
 * Zoom towards mouse position (for keyboard shortcuts)
 */
export function zoomTowardsMouse(zoomDelta, mouseX, mouseY) {
  if (!State.canvas) return;

  const rect = State.canvas.getBoundingClientRect();
  const canvasX = mouseX - rect.left;
  const canvasY = mouseY - rect.top;

  zoomTowards(zoomDelta, canvasX, canvasY);
}

// ============================================================
// Control Setup
// ============================================================

/**
 * Setup zoom controls
 */
export function setupZoomControls() {
  // These buttons are STATIC index.html elements that outlive the view.
  // The module-level listenersAttached flag resets on destroy(), so every
  // destroy+render cycle (mobile tab visits, view switches) re-ran this and
  // stacked duplicate listeners — one click then stepped twice or more.
  // Guard on the DOM element itself: it is the thing that persists.
  const controls = State.container?.querySelector("#m-zin")?.closest(".controls");
  if (controls?.dataset.mmWired === "1") return;
  if (controls) controls.dataset.mmWired = "1";

  const zin = State.container?.querySelector("#m-zin");
  const zout = State.container?.querySelector("#m-zout");
  // Button id in index.html is `m-reset` (matches title "Reset View and Layout").
  // The previous selector `#m-zreset` was a typo and never matched, leaving the
  // button inert. Restored here, with layout reset wired in too so the button
  // does what the title claims.
  const zreset = State.container?.querySelector("#m-reset");

  if (zin)
    zin.addEventListener("click", () => {
      zoomTowards(1.3);
    });

  if (zout)
    zout.addEventListener("click", () => {
      zoomTowards(1 / 1.3);
    });

  if (zreset)
    zreset.addEventListener("click", () => {
      // View: zoom 100%, no pan.
      State.setMState({ zoom: 1, panX: 0, panY: 0 });
      updateTransform({ animate: true });

      // Layout: clear per-node drag offsets on the focused subtree so the
      // default spacing takes over on the next render. Scoped to the
      // currently-focused subtree to keep the blast radius bounded —
      // other subtrees the user re-focuses later will still have their
      // original positions until they reset those too. mmSide (the
      // left/right preference for top-level children) is intentionally
      // preserved so re-balancing doesn't surprise the user.
      const focused = State.callbacks?.find?.(State.currentState?.focusId)
        || State.currentState?.data;
      if (focused && State.callbacks?.beginBatch && State.callbacks?.onNodeUpdate) {
        State.callbacks.beginBatch();
        try {
          const walk = (n) => {
            const hasOffset = n.mmOffsetX !== undefined || n.mmOffsetY !== undefined;
            const hasLegacy = n.mmX !== undefined || n.mmY !== undefined;
            if (hasOffset || hasLegacy) {
              State.callbacks.onNodeUpdate(n.id, {
                mmOffsetX: undefined,
                mmOffsetY: undefined,
                mmX: undefined,
                mmY: undefined,
              });
            }
            (n.children || []).forEach(walk);
          };
          walk(focused);
        } finally {
          State.callbacks.endBatch();
        }
      }

      // Re-render with the default layout.
      if (zoomCallbacks?.triggerRender) {
        zoomCallbacks.triggerRender();
      }
    });

  // Layout mode: cycle balanced → right → left. A local display preference:
  // the layout (Tidy.assignSides) applies it on every render and nothing is
  // written to the document, so switching back to balanced restores the
  // stored sides.
  const layoutBtn = State.container?.querySelector("#m-layout");
  if (layoutBtn) {
    const MODES = ["balanced", "right", "left"];
    const syncTitle = () => {
      const cur = Store.get("mindmapLayoutMode") || "balanced";
      layoutBtn.title = t('views.mindmap.layoutTitle', { mode: t(`views.mindmap.layout.${cur}`) });
      layoutBtn.textContent = cur === "right" ? "⇥" : cur === "left" ? "⇤" : "⇆";
    };
    syncTitle();
    layoutBtn.addEventListener("click", () => {
      const cur = Store.get("mindmapLayoutMode") || "balanced";
      const next = MODES[(MODES.indexOf(cur) + 1) % MODES.length];
      Store.setMindmapLayoutMode(next);
      State.setLayoutMode(next);
      syncTitle();
      if (zoomCallbacks?.triggerRender) zoomCallbacks.triggerRender();
    });
  }

  // Gap controls
  const gapUp = State.container?.querySelector("#m-gap-up");
  const gapDown = State.container?.querySelector("#m-gap-down");

  if (gapUp && zoomCallbacks?.increaseGap) {
    gapUp.addEventListener("click", zoomCallbacks.increaseGap);
  }

  if (gapDown && zoomCallbacks?.decreaseGap) {
    gapDown.addEventListener("click", zoomCallbacks.decreaseGap);
  }
}

// Document-level pan handler refs — module scope so a remount replaces the
// previous pair instead of stacking (see comment at the attach site).
let docPanMoveHandler = null;
let docPanUpHandler = null;

/**
 * Setup canvas pan and wheel zoom
 */
export function setupCanvasInteraction() {
  if (!State.canvas) return;
  // Same duplicate-listener hazard as setupZoomControls: #m-canvas is a
  // static element that outlives destroy()/render cycles — guard on it.
  if (State.canvas.dataset.mmWired === "1") return;
  State.canvas.dataset.mmWired = "1";

  let isPanning = false;
  let panStartX, panStartY;
  let lastWheelTime = 0;
  const wheelDebounce = 30; // ms - tuned based on actual event timing (13-47ms clusters)

  // Wheel zoom - zoom towards mouse pointer
  State.canvas.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();

      // Debounce: ignore rapid-fire events from one scroll gesture
      const now = Date.now();
      if (now - lastWheelTime < wheelDebounce) return;
      lastWheelTime = now;

      const rect = State.canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate world coordinates of mouse before zoom
      const worldX = (mouseX - State.mState.panX) / State.mState.zoom;
      const worldY = (mouseY - State.mState.panY) / State.mState.zoom;

      // Apply zoom using the same algorithm as original
      const prevZoom = State.mState.zoom;
      const delta = e.deltaY > 0 ? 1 / 1.2 : 1.2;
      const calculatedZoom = State.mState.zoom * delta;
      const newZoom = Math.max(0.1, Math.min(3, calculatedZoom));

      // If we're stuck at a limit, don't update (prevents jumping)
      if (newZoom === prevZoom) {
        return;
      }

      // Adjust pan using zoom ratio method (same as original)
      const zoomRatio = newZoom / prevZoom;
      State.setMState({
        zoom: newZoom,
        panX: mouseX - (mouseX - State.mState.panX) * zoomRatio,
        panY: mouseY - (mouseY - State.mState.panY) * zoomRatio
      });

      updateTransform({ animate: true });
    },
    { passive: false }
  );

  // Pan with mouse drag on canvas
  State.canvas.addEventListener("mousedown", (e) => {
    // Middle mouse button (button 1) always pans, even over nodes
    if (e.button === 1) {
      e.preventDefault();
      isPanning = true;
      panStartX = e.clientX - State.mState.panX;
      panStartY = e.clientY - State.mState.panY;
      State.canvas.style.cursor = "grabbing";
      // Disable transition during panning for immediate response
      if (State.world) State.world.style.transition = "none";
      return;
    }

    // Left mouse button (button 0) only pans on background
    if (
      e.button === 0 &&
      (e.target === State.canvas || e.target === State.world || e.target === State.svg)
    ) {
      isPanning = true;
      panStartX = e.clientX - State.mState.panX;
      panStartY = e.clientY - State.mState.panY;
      State.canvas.style.cursor = "grabbing";
      // Disable transition during panning for immediate response
      if (State.world) State.world.style.transition = "none";
    }
  });

  // Replace (don't stack) the document-level pan handlers: destroy() resets
  // State.listenersAttached, so every remount re-runs this setup — the
  // canvas listeners die with the canvas element, but document listeners
  // accumulated one pair per remount before this guard.
  if (docPanMoveHandler) document.removeEventListener("mousemove", docPanMoveHandler);
  if (docPanUpHandler) document.removeEventListener("mouseup", docPanUpHandler);

  docPanMoveHandler = (e) => {
    if (isPanning) {
      State.setMState({
        panX: e.clientX - panStartX,
        panY: e.clientY - panStartY
      });
      updateTransform();
    }
  };
  docPanUpHandler = () => {
    if (isPanning) {
      isPanning = false;
      if (State.canvas) State.canvas.style.cursor = "";
      // Re-enable transition after panning
      if (State.world) State.world.style.transition = "";
    }
  };

  document.addEventListener("mousemove", docPanMoveHandler);
  document.addEventListener("mouseup", docPanUpHandler);

  // Right-click on empty canvas/world/svg → empty-space context menu.
  // Per-node contextmenu listeners (in interaction.js) stopPropagation, so
  // this only fires for genuine background clicks.
  State.canvas.addEventListener("contextmenu", (e) => {
    if (e.target !== State.canvas && e.target !== State.world && e.target !== State.svg) {
      return;
    }
    e.preventDefault();
    zoomCallbacks?.onCanvasContextMenu?.(e.clientX, e.clientY);
  });
}
